import { Injectable } from '@angular/core';
import {
  groupByKeys,
  parseFunnelData,
  parseLinesChart,
  parseRangeData,
  parseRelativeData,
} from '@emma-helpers/parse-chart-data';
import {
  getWidgetChartTypes,
  getWidgetFilters,
  getWidgetGroupBys,
  getWidgetSubgroupBys,
} from '@emma-helpers/widget.helper';
import { DateChangeService } from '@platform/services/date-change.service';
import { ApiRelativeResponse, ApiSerialResponse } from 'emma-common-ts';
import {
  ChartRelativeInput,
  EMMA_EVENTS,
  FunnelData,
  WidgetChartType,
  WidgetConfig,
  WidgetDataParser,
  WidgetGetParams,
  WidgetGroupBy,
  WidgetKPI,
} from 'emma-common-ts/emma';
import { chain, get } from 'lodash';
import { Observable } from 'rxjs';
import { map, filter as rxFilter, switchMap } from 'rxjs/operators';

import { AppUserService } from './app-user.service';
import { CurrentAppService } from './current-app.service';
import { EventsService } from './events.service';
import { FunnelService, generateFunnelFiltersWidget } from './funnel.service';
import { TrackerService } from './tracker.service';

interface WidgetKPIs {
  [widgetName: string]: WidgetKPI;
}

/**
 * Define los parámetros de cada tipo de gráfica
 */
export const ChartTypes: { [k: string]: WidgetDataParser } = {
  lines:
    ({ title, label = title.toLocaleLowerCase(), key, rangeKey, index = 0 }, params = {}) =>
    (response: ApiSerialResponse | null) => {
      if (response && response.error) {
        return { error: response.error };
      } else {
        return parseLinesChart(
          {
            ...parseRangeData(response && response.data, key, rangeKey),
            labels: response ? response.dateLabels : [],
          },
          {
            label,
            title,
            index,
          }
        );
      }
    },
  value:
    ({ title, label = title.toLocaleLowerCase(), key, rangeKey, index = 0 }, params = {}) =>
    (response: ApiRelativeResponse | null): ChartRelativeInput => ({
      ...parseRelativeData(response && response.data),
      title,
      label,
      index,
    }),
  otherSerial:
    ({ key, rangeKey, title, label = title }, params = {}) =>
    (response: ApiSerialResponse | null) => {
      if (response && response.error) {
        return { error: response.error };
      }
      if (!params.groupBy) {
        return {
          ...parseRangeData(response && response.data, key, rangeKey),
          labels: response ? response.dateLabels : [],
          label,
        };
      } else {
        return {
          ...parseRangeData(
            response && response.data,
            key,
            get(groupByKeys, params.groupBy, params.groupBy),
            get(groupByKeys, params.subGroupBy, params.subGroupBy)
          ),
          label,
        };
      }
    },
  funnel:
    ({ title, label = title.toLocaleLowerCase(), index = 0 }, params = {}) =>
    (data: (FunnelData & { error: unknown }) | null): ChartRelativeInput => {
      if (data && data.error) {
        return { error: data.error as Error, index, label, total: 0, variation: 0 };
      } else {
        return {
          ...parseFunnelData(data),
          title,
          label,
          index,
        };
      }
    },
};
ChartTypes.mini = ChartTypes.otherSerial;
ChartTypes.bars = ChartTypes.otherSerial;
ChartTypes.donut = ChartTypes.otherSerial;
ChartTypes.halfdonut = ChartTypes.otherSerial;
ChartTypes.trapezoid = ChartTypes.value;

@Injectable({ providedIn: 'root' })
export class WidgetConfigService {
  chartTypes: WidgetChartType[];
  filters: { [filter: string]: WidgetGroupBy[] };
  groupBys: WidgetGroupBy[];
  subGroupBys: WidgetGroupBy[];
  currency = '';

  constructor(
    private trackerService: TrackerService,
    private appUserService: AppUserService,
    private eventsService: EventsService,
    private funnelService: FunnelService,
    private dateService: DateChangeService,
    private currentAppService: CurrentAppService
  ) {
    this.chartTypes = getWidgetChartTypes();
    this.filters = getWidgetFilters();
    this.groupBys = getWidgetGroupBys();
    this.subGroupBys = getWidgetSubgroupBys();
    const currentApp = this.currentAppService.getCurrentApp();
    this.currency = currentApp?.appConfig.currency ?? '';
  }

  /**
   * Genera el observable
   */
  getEmitter = (kpi: WidgetKPI[], typeCallback: WidgetDataParser, params: { [k: string]: unknown } = {}) => {
    return () => {
      let emitter: Observable<any>;
      // More than one KPI
      if (params.funnelData) {
        emitter = this.getFunnelEmitter(kpi).pipe(
          map((response) => {
            return response.data.map((d, i) => typeCallback(kpi[i], params)({ ...d, error: response.error }));
          })
        );
      } else if (params.relativeData) {
        emitter = this.getRelativeEmitter(kpi[0], params);
      } else {
        emitter = this.getUniqueEmitter(kpi[0], params);
      }
      // Funnel data comes already parsed
      if (!params.funnelData) {
        emitter = emitter.pipe(map(typeCallback(kpi[0], params)));
      }
      return emitter;
    };
  };

  private getRelativeEmitter(kpi: WidgetKPI, params: { [k: string]: unknown } = {}) {
    return kpi.requestRelative(null, params);
  }

  private getUniqueEmitter(kpi: WidgetKPI, params: { [k: string]: unknown } = {}) {
    // Just one KPI
    let emitter: Observable<any> = kpi.request(null, params);
    if (kpi.filter) {
      const { filter } = kpi;
      emitter = emitter.pipe(
        map((response: ApiSerialResponse | null) =>
          response
            ? {
                ...response,
                data: filter(response.data),
              }
            : null
        )
      );
    }
    return emitter;
  }

  private getFunnelEmitter(kpi: WidgetKPI[]) {
    return this.eventsService.getAllEvents().pipe(
      rxFilter((events) => Boolean(events)),
      map((events) => {
        if (events) {
          return chain(kpi)
            .map((k) => {
              if (k.eventKey) {
                const ev = events.data.find((_ev) => _ev.token === k.eventKey);
                return ev && ev.id;
              }
              return null;
            })
            .filter(Boolean)
            .value() as number[];
        }
        return [] as number[];
      }),
      switchMap((events) => {
        return this.dateService.getChangeEmitter().pipe(
          switchMap((dateRange) => {
            const filters = generateFunnelFiltersWidget(events, dateRange);
            return this.funnelService.resolveFunnelEvent(filters);
          })
        );
      })
    );
  }

  getKPIs(): WidgetKPIs {
    return {
      installs: {
        id: 'installs',
        title: $localize`Instalaciones`,
        key: 'installs',
        rangeKey: 'date',
        eventKey: EMMA_EVENTS.INSTALL,
        request: this.trackerService.getInstalls.bind(this.trackerService),
        requestRelative: this.trackerService.getInstallsRelative.bind(this.trackerService),
      },
      sessions: {
        id: 'sessions',
        title: $localize`Sesiones`,
        key: 'sessions',
        rangeKey: 'date',
        eventKey: EMMA_EVENTS.SESSION,
        request: this.appUserService.getSessions.bind(this.appUserService),
        requestRelative: this.appUserService.getSessionsRelative.bind(this.appUserService),
      },
      activeUsers: {
        id: 'activeUsers',
        title: $localize`Usuarios activos`,
        key: 'activeUsers',
        rangeKey: 'date',
        request: this.appUserService.getActiveUsers.bind(this.appUserService),
        requestRelative: this.appUserService.getActiveUsersRelative.bind(this.appUserService),
      },
      leads: {
        id: 'leads',
        title: $localize`Usuarios registrados`,
        key: 'leads',
        rangeKey: 'date',
        eventKey: EMMA_EVENTS.REGISTER,
        request: this.appUserService.getLeads.bind(this.appUserService),
        requestRelative: this.appUserService.getLeadsRelative.bind(this.appUserService),
      },
      buyers: {
        id: 'buyers',
        title: $localize`Compradores`,
        key: 'buyers',
        rangeKey: 'date',
        eventKey: EMMA_EVENTS.TRANSACTION,
        request: this.appUserService.getBuyers.bind(this.appUserService),
        requestRelative: this.appUserService.getBuyersRelative.bind(this.appUserService),
      },
      revenue: {
        id: 'revenue',
        title: $localize`Ingresos`,
        label: this.currency,
        key: 'revenue',
        rangeKey: 'date',
        request: this.trackerService.getRevenue.bind(this.trackerService),
        requestRelative: this.trackerService.getRevenueRelative.bind(this.trackerService),
      },
    };
  }

  /**
   * Unifica los datos de KPI, tipo de gráfica y observable, que definirán el widget.
   */
  get = ({
    kpi,
    type,
    layout = 'chart-only',
    index = 0,
    groupBy = '',
    subGroupBy = '',
    filter = {},
  }: WidgetGetParams): WidgetConfig | undefined => {
    const KPIs = this.getKPIs();
    const typeCallback = ChartTypes[type];
    let kpiData;
    if ('string' === typeof kpi) {
      if (!KPIs[kpi] || !typeCallback) {
        return undefined;
      }
      kpiData = [{ ...KPIs[kpi], index: Number(index) }];
    } else {
      if (kpi.find((k) => !KPIs[k])) {
        return undefined;
      }
      kpiData = kpi.map((k) => ({ ...KPIs[k], index: Number(index) }));
    }

    const params = { ...filter };
    if (groupBy) {
      params.groupBy = groupBy;
    }
    if (subGroupBy) {
      params.subGroupBy = subGroupBy;
    }
    return {
      emitter: this.getEmitter(kpiData, typeCallback, {
        relativeData: type === 'value' || type === 'trapezoid',
        funnelData: type === 'funnel',
        ...params,
      }),
      type,
      layout,
      kpi: kpiData,
    };
  };
}
